import { useEffect, useState } from 'react';
import { usePlatformStore } from '@/lib/store';
import { Tabs } from '@/components/shared/UIComponents';
import { TenantOverridesTab } from './feature-gating/TenantOverridesTab';
import { UserOverridesTab } from './feature-gating/UserOverridesTab';
import { PricingTab } from './feature-gating/PricingTab';
import { AuditLogTab } from './feature-gating/AuditLogTab';

// The "default" tenant is the first tenant — used for global defaults.
// In production this would be a dedicated "template" tenant or the platform's own config.
const DEFAULT_TENANT_ID_KEY = 'fg_default_tenant';

export function FeatureGatingPage() {
  const { tenants, loadTenants } = usePlatformStore();
  const [tab, setTab] = useState('tenants');

  useEffect(() => { loadTenants(); }, [loadTenants]);

  // Use the first tenant as the "default" tenant for global plan defaults.
  // Persist selection so it survives tab switches.
  const [defaultTenantId, setDefaultTenantId] = useState(() => {
    return sessionStorage.getItem(DEFAULT_TENANT_ID_KEY) || '';
  });

  useEffect(() => {
    if (!defaultTenantId && tenants.length > 0) {
      const id = tenants[0].id;
      setDefaultTenantId(id);
      sessionStorage.setItem(DEFAULT_TENANT_ID_KEY, id);
    }
  }, [tenants, defaultTenantId]);

  const tabs = [
    { id: 'tenants', label: 'Tenant Overrides' },
    { id: 'users', label: 'User Overrides' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'audit', label: 'Audit Log' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Feature Gating</h1>
          <p className="text-surface-500 mt-1">
            Two-tier admin control — set global defaults, per-tenant overrides, user-level exceptions, and pricing
          </p>
        </div>

        {/* Default tenant selector */}
        {tenants.length > 1 && (
          <div className="flex items-center gap-2 text-xs text-surface-500">
            <span>Default Tenant:</span>
            <select
              value={defaultTenantId}
              onChange={e => {
                setDefaultTenantId(e.target.value);
                sessionStorage.setItem(DEFAULT_TENANT_ID_KEY, e.target.value);
              }}
              className="input-field py-1 text-xs w-40"
            >
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {!defaultTenantId ? (
        <div className="text-center py-12 text-sm text-surface-500">
          Loading tenants...
        </div>
      ) : (
        <>
          {tab === 'tenants' && <TenantOverridesTab defaultTenantId={defaultTenantId} />}
          {tab === 'users' && <UserOverridesTab defaultTenantId={defaultTenantId} />}
          {tab === 'pricing' && <PricingTab defaultTenantId={defaultTenantId} />}
          {tab === 'audit' && <AuditLogTab />}
        </>
      )}
    </div>
  );
}
